/** L_SHAREHOLDER_DETAIL.SHAREHOLDER_JSON_DATA ↔ MembersSection 지분 UI (Member shares) */

import {
    parseTransferDetailsFromRaw,
    normalizeTransferDetailsArray,
    normalizeTransferDetailRowSharesForSave,
    resolveTransferDetailsFromRecord,
    syncTransferRootFromDetails,
} from './myRegisterTransferDetails';

export const MEMBER_SHARE_JSON_ROOT_KEYS = [
    'TRANSACTION_DATE',
    'TRANSACTION_NO',
    'ACQ_CERTI_NO',
    'ACQ_DISTINCT_NO_FROM',
    'ACQ_DISTINCT_NO_TO',
    'ACQ_NUMBER_OF_SHARES',
    'ACQ_ISSUED_SHARE_CAPITAL',
    'ACQ_PAID_UP_SHARE_CAPITAL',
    'ACQ_TC',
    'TRF_CERTI_NO',
    'TRF_DISTINCT_NO_FROM',
    'TRF_DISTINCT_NO_TO',
    'TRF_NUMBER_OF_SHARES',
    'TRF_ISSUED_SHARE_CAPITAL',
    'TRF_PAID_UP_SHARE_CAPITAL',
    'TRF_TC',
    'TFOR_NAME',
    'TEE_NAME',
    'TFOR_REG_NO',
    'TEE_REG_NO',
    'TFOR_CONTACT_CD',
    'TEE_CONTACT_CD',
    'TFOR_FOLIO_NO',
    'TEE_FOLIO_NO',
] as const;

export function isAllotShareEventType(eventType: unknown): boolean {
    return String(eventType ?? '').trim().toLowerCase() === 'allot';
}

function resolveShareInfoFromRow(shareRow: Record<string, unknown>): Record<string, unknown> {
    const parsed = parseShareholderJsonRoot(shareRow.SHAREHOLDER_JSON_DATA ?? null);
    const fromJson = parsed.share_info && typeof parsed.share_info === 'object'
        ? parsed.share_info as Record<string, unknown>
        : {};
    const fromRow = shareRow.share_info && typeof shareRow.share_info === 'object'
        ? shareRow.share_info as Record<string, unknown>
        : {};
    return { ...fromJson, ...fromRow };
}

export function isAllotShareRow(shareRow: Record<string, unknown>, eventType?: unknown): boolean {
    if (isAllotShareEventType(eventType)) return true;
    const shareInfo = resolveShareInfoFromRow(shareRow);
    return isAllotShareEventType(shareInfo.event_type ?? shareInfo.eventType);
}

/** Allot 이벤트 — Shares Acquired(TFOR)에 Member 본인 이름/등록번호 자동 채움 */
export function applyAllotAcquiredPartyFromMember(
    shareRow: Record<string, unknown>,
    memberContact: Record<string, unknown>,
    options?: { eventType?: unknown },
): void {
    if (!isAllotShareRow(shareRow, options?.eventType)) return;

    const name = String(memberContact.CONTACT_NAME ?? memberContact.contactName ?? '').trim();
    const regNo = String(memberContact.CONTACT_REG_NO ?? memberContact.contactRegNo ?? '').trim();
    const contactCd = String(memberContact.CONTACT_CD ?? memberContact.contactCd ?? '').trim();
    if (!name && !regNo) return;

    if (!String(shareRow.TFOR_NAME ?? '').trim()) shareRow.TFOR_NAME = name;
    if (!String(shareRow.TFOR_REG_NO ?? '').trim()) shareRow.TFOR_REG_NO = regNo;
    if (contactCd && !String(shareRow.TFOR_CONTACT_CD ?? '').trim()) {
        shareRow.TFOR_CONTACT_CD = contactCd;
    }
}

function pickShareText(shareData: Record<string, unknown>, ...keys: string[]): string | null {
    for (const key of keys) {
        const v = shareData[key];
        if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return null;
}

/** UI shareData → SHAREHOLDER_JSON_DATA 루트 (Transfer 레지스터 TFOR/TEE 바인딩과 동일) */
export function buildMemberShareholderJsonRoot(shareData: Record<string, unknown>): Record<string, unknown> {
    const root: Record<string, unknown> = {};
    const set = (targetKey: string, ...sourceKeys: string[]) => {
        const value = pickShareText(shareData, ...sourceKeys);
        if (value != null) root[targetKey] = value;
    };

    set('TRANSACTION_DATE', 'TRANSACTION_DATE');
    set('TRANSACTION_NO', 'TRANSACTION_NO');
    set('ACQ_CERTI_NO', 'AC_CERTIFICATE_NO', 'ACQ_CERTI_NO', 'CERTI_NO', 'CERT_NO');
    set('ACQ_NUMBER_OF_SHARES', 'AC_NUMBER_OF_SHARES', 'ACQ_NUMBER_OF_SHARES');
    set('ACQ_ISSUED_SHARE_CAPITAL', 'AC_ISSUED_SHARE_CAPITAL', 'ACQ_ISSUED_SHARE_CAPITAL');
    set('ACQ_PAID_UP_SHARE_CAPITAL', 'AC_PAID_UP_SHARE_CAPITAL', 'ACQ_PAID_UP_SHARE_CAPITAL');
    set('ACQ_TC', 'AC_TC', 'ACQ_TC');
    set('TRF_CERTI_NO', 'TR_CERTIFICATE_NO', 'TRF_CERTI_NO');
    set('TRF_NUMBER_OF_SHARES', 'TR_NUMBER_OF_SHARES', 'TRF_NUMBER_OF_SHARES');
    set('TRF_ISSUED_SHARE_CAPITAL', 'TR_ISSUED_SHARE_CAPITAL', 'TRF_ISSUED_SHARE_CAPITAL');
    set('TRF_PAID_UP_SHARE_CAPITAL', 'TR_PAID_UP_SHARE_CAPITAL', 'TRF_PAID_UP_SHARE_CAPITAL');
    set('TRF_TC', 'TR_TC', 'TRF_TC');
    set('TFOR_NAME', 'TFOR_NAME');
    set('TEE_NAME', 'TEE_NAME');
    set('TFOR_REG_NO', 'TFOR_REG_NO');
    set('TEE_REG_NO', 'TEE_REG_NO');
    set('TFOR_CONTACT_CD', 'TFOR_CONTACT_CD');
    set('TEE_CONTACT_CD', 'TEE_CONTACT_CD');
    set('TFOR_FOLIO_NO', 'TFOR_FOLIO_NO', 'FOLIO_NO', 'FOLIO');
    set('TEE_FOLIO_NO', 'TEE_FOLIO_NO');
    set('TFOR_CERTI_NO', 'TFOR_CERTI_NO', 'TRF_CERTI_NO', 'TR_CERTIFICATE_NO');
    set('TEE_CERTI_NO', 'TEE_CERTI_NO', 'AC_CERTIFICATE_NO', 'ACQ_CERTI_NO');
    set('TFOR_NUMBER_OF_SHARES', 'TFOR_NUMBER_OF_SHARES', 'TRF_NUMBER_OF_SHARES', 'TR_NUMBER_OF_SHARES');
    set('TEE_NUMBER_OF_SHARES', 'TEE_NUMBER_OF_SHARES', 'AC_NUMBER_OF_SHARES', 'ACQ_NUMBER_OF_SHARES');

    return root;
}

export function parseShareholderJsonRoot(raw: unknown): Record<string, unknown> {
    if (raw == null || raw === '') return { version: 1, share_info: {} };
    try {
        const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!obj || typeof obj !== 'object') return { version: 1, share_info: {} };
        if (!obj.share_info || typeof obj.share_info !== 'object') obj.share_info = {};
        return obj as Record<string, unknown>;
    } catch {
        return { version: 1, share_info: {} };
    }
}

/** SHAREHOLDER_JSON_DATA 루트 → MembersSection AC/TR UI 필드 */
export function applyMemberShareJsonRootToShareRow(shareRow: Record<string, unknown>, rawJsonData: unknown): void {
    const root = parseShareholderJsonRoot(rawJsonData);
    const shareInfo = (shareRow.share_info && typeof shareRow.share_info === 'object'
        ? shareRow.share_info
        : root.share_info) as Record<string, unknown> | undefined;
    const transferIn = isTransferInShareRow(shareInfo || {});
    const transferOut = isTransferOutShareRow(shareInfo || {});

    for (const key of MEMBER_SHARE_JSON_ROOT_KEYS) {
        if (transferIn && (key.startsWith('TRF_') || key === 'TEE_NAME' || key === 'TEE_REG_NO' || key === 'TEE_CONTACT_CD')) {
            continue;
        }
        if (transferOut && (key.startsWith('ACQ_') || key === 'TFOR_NAME' || key === 'TFOR_REG_NO' || key === 'TFOR_CONTACT_CD')) {
            continue;
        }
        const v = root[key];
        if (v != null && String(v).trim() !== '') {
            shareRow[key] = v;
        }
    }

    if (!String(shareRow.AC_CERTIFICATE_NO ?? '').trim()) {
        shareRow.AC_CERTIFICATE_NO = root.ACQ_CERTI_NO ?? shareRow.ACQ_CERTI_NO ?? '';
    }
    if (!String(shareRow.AC_NUMBER_OF_SHARES ?? '').trim()) {
        shareRow.AC_NUMBER_OF_SHARES = root.ACQ_NUMBER_OF_SHARES ?? shareRow.ACQ_NUMBER_OF_SHARES ?? '';
    }
    if (!String(shareRow.AC_ISSUED_SHARE_CAPITAL ?? '').trim()) {
        shareRow.AC_ISSUED_SHARE_CAPITAL = root.ACQ_ISSUED_SHARE_CAPITAL ?? shareRow.ACQ_ISSUED_SHARE_CAPITAL ?? '';
    }
    if (!String(shareRow.AC_PAID_UP_SHARE_CAPITAL ?? '').trim()) {
        shareRow.AC_PAID_UP_SHARE_CAPITAL = root.ACQ_PAID_UP_SHARE_CAPITAL ?? shareRow.ACQ_PAID_UP_SHARE_CAPITAL ?? '';
    }
    if (!transferIn) {
        if (!String(shareRow.TR_CERTIFICATE_NO ?? '').trim()) {
            shareRow.TR_CERTIFICATE_NO = root.TRF_CERTI_NO ?? shareRow.TRF_CERTI_NO ?? '';
        }
        if (!String(shareRow.TR_NUMBER_OF_SHARES ?? '').trim()) {
            shareRow.TR_NUMBER_OF_SHARES = root.TRF_NUMBER_OF_SHARES ?? shareRow.TRF_NUMBER_OF_SHARES ?? '';
        }
        if (!String(shareRow.TR_ISSUED_SHARE_CAPITAL ?? '').trim()) {
            shareRow.TR_ISSUED_SHARE_CAPITAL = root.TRF_ISSUED_SHARE_CAPITAL ?? shareRow.TRF_ISSUED_SHARE_CAPITAL ?? '';
        }
        if (!String(shareRow.TR_PAID_UP_SHARE_CAPITAL ?? '').trim()) {
            shareRow.TR_PAID_UP_SHARE_CAPITAL = root.TRF_PAID_UP_SHARE_CAPITAL ?? shareRow.TRF_PAID_UP_SHARE_CAPITAL ?? '';
        }
        if (!String(shareRow.TEE_NAME ?? '').trim() && root.TEE_NAME) {
            shareRow.TEE_NAME = root.TEE_NAME;
        }
        if (!String(shareRow.TEE_REG_NO ?? '').trim() && root.TEE_REG_NO) {
            shareRow.TEE_REG_NO = root.TEE_REG_NO;
        }
    }
    if (!transferOut) {
        if (!String(shareRow.TFOR_NAME ?? '').trim() && root.TFOR_NAME) {
            shareRow.TFOR_NAME = root.TFOR_NAME;
        }
        if (!String(shareRow.TFOR_REG_NO ?? '').trim() && root.TFOR_REG_NO) {
            shareRow.TFOR_REG_NO = root.TFOR_REG_NO;
        }
    }

    const transferDetails = parseTransferDetailsFromRaw(rawJsonData);
    if (transferDetails.length > 0) {
        shareRow.transfer_details = transferDetails;
    }

    sanitizeMemberShareRowForTransferDirection(shareRow, rawJsonData);
}

export function mergeMemberShareIntoShareholderJson(
    existingRaw: unknown,
    shareData: Record<string, unknown>,
    shareInfo: Record<string, unknown>,
): Record<string, unknown> {
    const root = parseShareholderJsonRoot(existingRaw);
    const patch = buildMemberShareholderJsonRoot(shareData);
    for (const key of MEMBER_SHARE_JSON_ROOT_KEYS) {
        if (patch[key] !== undefined) root[key] = patch[key];
    }
    for (const extraKey of [
        'TFOR_CERTI_NO',
        'TEE_CERTI_NO',
        'TFOR_NUMBER_OF_SHARES',
        'TEE_NUMBER_OF_SHARES',
    ] as const) {
        if (patch[extraKey] !== undefined) root[extraKey] = patch[extraKey];
    }
    root.share_info = {
        ...(root.share_info && typeof root.share_info === 'object' ? root.share_info as Record<string, unknown> : {}),
        ...shareInfo,
    };
    const transferDetails = normalizeTransferDetailsArray(resolveTransferDetailsFromRecord(shareData))
        .map((detail) => normalizeTransferDetailRowSharesForSave(detail));
    if (transferDetails.length > 0) {
        root.transfer_details = transferDetails;
        const synced = syncTransferRootFromDetails(root, transferDetails);
        if (synced.version == null) synced.version = 1;
        return synced;
    }
    if (root.version == null) root.version = 1;
    return root;
}

const CLASS_CODE_TO_DISPLAY: Record<string, string> = {
    A20001: 'Ordinary',
    A20002: 'Preference',
    A20003: 'Others',
};

function pickShareNumericText(...values: unknown[]): string {
    for (const v of values) {
        const raw = String(v ?? '').replace(/,/g, '').trim();
        if (!raw) continue;
        const n = Number(raw);
        if (Number.isFinite(n)) {
            return Number.isInteger(n) ? String(n) : raw;
        }
        return raw;
    }
    return '';
}

function parseShareUnitsNumber(value: unknown): number {
    const text = pickShareNumericText(value);
    if (!text) return 0;
    const n = Number(text.replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}

/** share_info — total_unit_of_shares 우선 (cash/otherwise는 total 없을 때만) */
function sumShareInfoSectionUnits(info: Record<string, unknown> | null): number {
    if (!info) return 0;
    const children = info.share_info_items;
    if (Array.isArray(children) && children.length > 0) {
        return children.reduce((sum, child) => {
            const c = child && typeof child === 'object' ? child as Record<string, unknown> : {};
            const sectionTotal = parseShareUnitsNumber(c.total_unit_of_shares ?? c.totalUnitOfShares);
            if (sectionTotal > 0) return sum + sectionTotal;
            return sum + parseShareUnitsNumber(c.cash) + parseShareUnitsNumber(c.otherwise);
        }, 0);
    }
    const total = parseShareUnitsNumber(info.total_unit_of_shares ?? info.totalUnitOfShares);
    if (total > 0) return total;
    return parseShareUnitsNumber(info.cash) + parseShareUnitsNumber(info.otherwise);
}

function isTransferMemberShareOutRow(share: Record<string, unknown>, info: Record<string, unknown>): boolean {
    const held = String(info.held ?? '').trim().toLowerCase();
    if (held === 'transferor') return true;
    if (held === 'holder') return false;

    const eventType = String(info.event_type ?? info.eventType ?? '').trim().toLowerCase();
    if (eventType !== 'transfer') return false;

    const trUnits = parseShareUnitsNumber(share.TR_NUMBER_OF_SHARES ?? share.TRF_NUMBER_OF_SHARES);
    const acUnits = parseShareUnitsNumber(share.AC_NUMBER_OF_SHARES ?? share.ACQ_NUMBER_OF_SHARES);
    return trUnits > 0 && acUnits <= 0;
}

/** Transfer-in (양수) — Shares Acquired 로 취득 */
export function isMemberShareTransferIn(
    share: Record<string, unknown>,
    info?: Record<string, unknown>,
): boolean {
    const resolved = info && typeof info === 'object'
        ? info
        : resolveShareInfoFromRow(share);
    if (isTransferMemberShareOutRow(share, resolved)) return false;

    const eventType = String(resolved.event_type ?? resolved.eventType ?? '').trim().toLowerCase();
    if (eventType === 'transfer') return true;

    const root = parseShareholderJsonRoot(share.SHAREHOLDER_JSON_DATA ?? null);
    const details = normalizeTransferDetailsArray(
        share.transfer_details ?? root.transfer_details,
    );
    if (details.length > 0) return true;

    const held = String(resolved.held ?? '').trim().toLowerCase();
    if (held === 'holder') {
        const hasTfor = String(share.TFOR_CONTACT_CD ?? root.TFOR_CONTACT_CD ?? root.TFOR_NAME ?? '').trim();
        const hasTee = String(share.TEE_CONTACT_CD ?? root.TEE_CONTACT_CD ?? root.TEE_NAME ?? '').trim();
        if (hasTfor && hasTee) return true;
    }

    return false;
}

/** Transfer-out (양도) — Shares Transferred 섹션 표시 */
export function isMemberShareTransferOut(
    share: Record<string, unknown>,
    info?: Record<string, unknown>,
): boolean {
    const resolved = info && typeof info === 'object'
        ? info
        : resolveShareInfoFromRow(share);
    if (isMemberShareTransferIn(share, resolved)) return false;
    return isTransferMemberShareOutRow(share, resolved);
}

function isTransferInShareRow(shareInfo: Record<string, unknown>): boolean {
    return isMemberShareTransferIn({}, shareInfo);
}

function isTransferOutShareRow(shareInfo: Record<string, unknown>): boolean {
    const held = String(shareInfo?.held ?? '').trim().toLowerCase();
    return held === 'transferor';
}

/** Transfer-in(양수) — Shares Transferred(TR/TEE) 필드 비움 */
export function sanitizeMemberShareRowForTransferDirection(
    shareRow: Record<string, unknown>,
    rawJsonData?: unknown,
): void {
    const root = parseShareholderJsonRoot(rawJsonData ?? shareRow.SHAREHOLDER_JSON_DATA ?? null);
    const shareInfo = (shareRow.share_info && typeof shareRow.share_info === 'object'
        ? shareRow.share_info
        : root.share_info) as Record<string, unknown> | undefined;
    if (!isTransferInShareRow(shareInfo || {})) return;

    const clearKeys = [
        'TR_CERTIFICATE_NO', 'TRF_CERTI_NO',
        'TR_NUMBER_OF_SHARES', 'TRF_NUMBER_OF_SHARES',
        'TR_ISSUED_SHARE_CAPITAL', 'TRF_ISSUED_SHARE_CAPITAL',
        'TR_PAID_UP_SHARE_CAPITAL', 'TRF_PAID_UP_SHARE_CAPITAL',
        'TR_TC', 'TRF_TC',
        'TR_DISTINCTIVE_NOS_FROM', 'TR_DISTINCTIVE_NOS_TO',
        'TRF_DISTINCT_NO_FROM', 'TRF_DISTINCT_NO_TO',
        'TEE_NAME', 'TEE_REG_NO', 'TEE_CONTACT_CD', 'TEE_CERTI_NO',
    ] as const;
    for (const key of clearKeys) {
        shareRow[key] = '';
    }
}

function isTransferMemberShareRow(share: Record<string, unknown>, info: Record<string, unknown>): boolean {
    return isTransferMemberShareOutRow(share, info);
}

/** 목록/합계 — NUMBER OF SHARES (total_unit_of_shares) 표시용 */
export function resolveMemberShareAcquiredUnits(share: Record<string, unknown>): number {
    const info = resolveShareInfoFromRow(share);
    if (isTransferMemberShareRow(share, info)) {
        return 0;
    }

    const fromShareInfo = sumShareInfoSectionUnits(info);
    if (fromShareInfo > 0) return fromShareInfo;

    const fromDbBuckets = parseShareUnitsNumber(share.SHARES_ORDINARY)
        + parseShareUnitsNumber(share.SHARES_PREFERENCE)
        + parseShareUnitsNumber(share.SHARES_OTHERS);
    if (fromDbBuckets > 0) return fromDbBuckets;

    return parseShareUnitsNumber(share.AC_NUMBER_OF_SHARES ?? share.ACQ_NUMBER_OF_SHARES);
}

export function resolveMemberShareTransferredUnits(share: Record<string, unknown>): number {
    const info = resolveShareInfoFromRow(share);
    if (!isTransferMemberShareRow(share, info)) {
        return parseShareUnitsNumber(share.TR_NUMBER_OF_SHARES ?? share.TRF_NUMBER_OF_SHARES);
    }

    const fromShareInfo = sumShareInfoSectionUnits(info);
    if (fromShareInfo > 0) return fromShareInfo;

    return parseShareUnitsNumber(share.TR_NUMBER_OF_SHARES ?? share.TRF_NUMBER_OF_SHARES);
}

function resolveShareClassName(share: Record<string, unknown>, shareInfo: Record<string, unknown>): string {
    const category = shareInfo.share_category;
    if (category && typeof category === 'object') {
        const name = String((category as Record<string, unknown>).name ?? '').trim();
        if (name) return name;
    }
    const code = String(share.CLASS_OF_SHARES ?? '').trim();
    return CLASS_CODE_TO_DISPLAY[code] || code || 'Ordinary';
}

/** MembersSection 지분 행 → SHAREHOLDER_JSON_DATA.share_info (Transfer 레지스터 표시용) */
export function buildShareInfoFromMemberShareRow(
    share: Record<string, unknown>,
    existingShareInfo: Record<string, unknown> = {},
): Record<string, unknown> {
    const si = existingShareInfo && typeof existingShareInfo === 'object' ? existingShareInfo : {};
    const eventTypeLower = String(si.event_type ?? si.eventType ?? '').trim().toLowerCase();
    const isTransferOut = isMemberShareTransferOut(share, si);
    const isTransfer = eventTypeLower === 'transfer' || isTransferOut;
    const isTransferIn = eventTypeLower === 'transfer' && !isTransferOut;

    const transferDetails = normalizeTransferDetailsArray(share.transfer_details);
    const totalFromDetails = isTransferIn && transferDetails.length > 0
        ? transferDetails.reduce((sum, d) => {
            const n = Number(String(d.TFOR_NUMBER_OF_SHARES ?? '').replace(/,/g, ''));
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0)
        : 0;
    const considerationFromDetails = isTransferIn && transferDetails.length > 0
        ? transferDetails.reduce((sum, d) => {
            const raw = pickShareNumericText(d.ConsiderAmt, d.NominalAmt, d.AmtPaid);
            const n = Number(raw.replace(/,/g, ''));
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0)
        : 0;

    const certNo = (() => {
        const fromSi = String(si.cert_no ?? si.certNo ?? '').trim();
        if (fromSi) return fromSi;
        return String(
            share.AC_CERTIFICATE_NO
            ?? share.ACQ_CERTI_NO
            ?? share.CERTI_NO
            ?? share.CERT_NO
            ?? share.TR_CERTIFICATE_NO
            ?? share.TRF_CERTI_NO
            ?? '',
        ).trim();
    })();

    const totalUnits = pickShareNumericText(
        totalFromDetails > 0 ? String(totalFromDetails) : null,
        si.total_unit_of_shares,
        si.totalUnitOfShares,
        share.AC_NUMBER_OF_SHARES,
        share.ACQ_NUMBER_OF_SHARES,
        share.TR_NUMBER_OF_SHARES,
        share.TRF_NUMBER_OF_SHARES,
    );

    const consideration = pickShareNumericText(
        considerationFromDetails > 0 ? String(considerationFromDetails) : null,
        si.consideration_amount,
        si.considerationAmount,
        share.AC_PAID_UP_SHARE_CAPITAL,
        share.ACQ_PAID_UP_SHARE_CAPITAL,
        share.AC_ISSUED_SHARE_CAPITAL,
        share.ACQ_ISSUED_SHARE_CAPITAL,
    );

    const nominal = pickShareNumericText(
        si.nominal_amount_each_share,
        si.nominalAmountEachShare,
    );

    const paidEach = pickShareNumericText(
        si.amount_paid_each_share,
        si.amountPaidEachShare,
        nominal,
    );

    return {
        cert_no: certNo,
        event_no: String(si.event_no ?? si.eventNo ?? share.TRANSACTION_NO ?? '').trim(),
        event_type: String(si.event_type ?? si.eventType ?? (isTransfer ? 'Transfer' : 'Allot')).trim(),
        share_category: si.share_category ?? { name: resolveShareClassName(share, si) },
        type: String(si.type ?? 'Shares').trim() || 'Shares',
        held: String(si.held ?? (isTransferOut ? 'Transferor' : 'Holder')).trim() || 'Holder',
        total_unit_of_shares: totalUnits,
        nominal_amount_each_share: nominal || paidEach,
        amount_paid_each_share: paidEach || nominal,
        amount_due_payable_each_share: String(si.amount_due_payable_each_share ?? si.amountDuePayableEachShare ?? '0.00').trim() || '0.00',
        cash: si.cash ?? (totalUnits || ''),
        otherwise: si.otherwise ?? 0,
        consideration_amount: consideration || paidEach || totalUnits,
    };
}

export function buildMemberShareholderJsonFromShareRow(share: Record<string, unknown>): Record<string, unknown> {
    const details = normalizeTransferDetailsArray(resolveTransferDetailsFromRecord(share))
        .map((detail) => normalizeTransferDetailRowSharesForSave(detail));

    if (share.shareholderJsonData && typeof share.shareholderJsonData === 'object') {
        const direct = { ...(share.shareholderJsonData as Record<string, unknown>) };
        const directSi = direct.share_info;
        if (directSi && typeof directSi === 'object') {
            const cert = String((directSi as Record<string, unknown>).cert_no ?? (directSi as Record<string, unknown>).certNo ?? '').trim();
            if (cert || String((directSi as Record<string, unknown>).event_type ?? '').trim()) {
                const mergedExistingSi = {
                    ...(directSi as Record<string, unknown>),
                    ...(share.share_info && typeof share.share_info === 'object' ? share.share_info as Record<string, unknown> : {}),
                };
                const shareInfo = buildShareInfoFromMemberShareRow(share, mergedExistingSi);
                let root = mergeMemberShareIntoShareholderJson(direct, share, shareInfo);
                if (details.length > 0) {
                    root.transfer_details = details;
                    root = syncTransferRootFromDetails(root, details);
                }
                return root;
            }
        }
    }

    const parsed = parseShareholderJsonRoot(share.SHAREHOLDER_JSON_DATA ?? null);
    const mergedExistingSi = {
        ...(parsed.share_info && typeof parsed.share_info === 'object' ? parsed.share_info as Record<string, unknown> : {}),
        ...(share.share_info && typeof share.share_info === 'object' ? share.share_info as Record<string, unknown> : {}),
    };
    const shareInfo = buildShareInfoFromMemberShareRow(share, mergedExistingSi);
    const root = mergeMemberShareIntoShareholderJson(share.SHAREHOLDER_JSON_DATA ?? null, share, shareInfo);
    if (details.length > 0) {
        root.transfer_details = details;
        return syncTransferRootFromDetails(root, details);
    }
    return root;
}

function pickShareInfoCertNo(shareInfo: Record<string, unknown> | null | undefined): string {
    if (!shareInfo) return '';
    return String(shareInfo.cert_no ?? shareInfo.certNo ?? '').trim();
}

/** UI 목록 표시용 Cert No */
export function resolveMemberShareCertNoForDisplay(share: Record<string, unknown>): string {
    const acCert = String(share.AC_CERTIFICATE_NO ?? '').trim();
    if (acCert) return acCert;

    const fromRowShareInfo = pickShareInfoCertNo(
        share.share_info && typeof share.share_info === 'object'
            ? share.share_info as Record<string, unknown>
            : null,
    );
    if (fromRowShareInfo) return fromRowShareInfo;

    const jsonRoot = share.shareholderJsonData ?? share.SHAREHOLDER_JSON_DATA;
    if (jsonRoot && typeof jsonRoot === 'object') {
        const si = (jsonRoot as Record<string, unknown>).share_info;
        const fromJson = pickShareInfoCertNo(
            si && typeof si === 'object' ? si as Record<string, unknown> : null,
        );
        if (fromJson) return fromJson;
    }

    return String(
        share.ACQ_CERTI_NO
        ?? share.TR_CERTIFICATE_NO
        ?? share.TRF_CERTI_NO
        ?? share.CERTI_NO
        ?? share.CERT_NO
        ?? '',
    ).trim();
}

/** Apply to list / 저장 시 Cert No 관련 필드 일괄 동기화 */
export function syncMemberShareCertNoFields(
    shareData: Record<string, unknown>,
    certNo?: string,
): void {
    const next = String(
        certNo
        ?? shareData.AC_CERTIFICATE_NO
        ?? pickShareInfoCertNo(
            shareData.share_info && typeof shareData.share_info === 'object'
                ? shareData.share_info as Record<string, unknown>
                : null,
        )
        ?? '',
    ).trim();
    if (!next) return;

    shareData.AC_CERTIFICATE_NO = next;
    shareData.ACQ_CERTI_NO = next;
    shareData.CERT_NO = next;
    shareData.CERTI_NO = next;

    if (shareData.share_info && typeof shareData.share_info === 'object') {
        (shareData.share_info as Record<string, unknown>).cert_no = next;
    }

    const jsonField = shareData.shareholderJsonData ?? shareData.SHAREHOLDER_JSON_DATA;
    if (jsonField && typeof jsonField === 'object') {
        const root = { ...(jsonField as Record<string, unknown>) };
        const shareInfo = root.share_info && typeof root.share_info === 'object'
            ? { ...(root.share_info as Record<string, unknown>) }
            : {};
        shareInfo.cert_no = next;
        root.share_info = shareInfo;
        root.ACQ_CERTI_NO = next;
        shareData.shareholderJsonData = root;
        shareData.SHAREHOLDER_JSON_DATA = root;
    }
}

/** sharesList 행 식별 — Apply to list 시 동일 행 찾기 (Cert No 변경 후에도 유지되는 stable key) */
export function buildMemberShareListRowKey(share: Record<string, unknown>): string {
    const stable = String(share._listRowKey ?? '').trim();
    if (stable) return stable;

    const shareholderIdx = Number(share.SHAREHOLDER_IDX ?? 0);
    const shareCapitalIdx = Number(share.SHARE_CAPITAL_IDX ?? 0);
    const cert = String(
        share.CERT_NO
        ?? share.CERTI_NO
        ?? share.AC_CERTIFICATE_NO
        ?? share.ACQ_CERTI_NO
        ?? pickShareInfoCertNo(
            share.share_info && typeof share.share_info === 'object'
                ? share.share_info as Record<string, unknown>
                : null,
        )
        ?? '',
    ).trim();
    const txnDate = String(share.TRANSACTION_DATE ?? '').trim();
    return `${shareholderIdx}|${shareCapitalIdx}|${cert}|${txnDate}`;
}

export function ensureMemberShareListRowKey(share: Record<string, unknown>): string {
    const existing = String(share._listRowKey ?? '').trim();
    if (existing) return existing;

    const displayEventKey = String(share._DISPLAY_EVENT_KEY ?? '').trim();
    if (displayEventKey) {
        const key = `display-transfer-out-${displayEventKey}`;
        share._listRowKey = key;
        return key;
    }

    const shareholderIdx = Number(share.SHAREHOLDER_IDX ?? 0);
    if (shareholderIdx > 0) {
        const key = `shareholder-${shareholderIdx}`;
        share._listRowKey = key;
        return key;
    }

    const key = buildMemberShareListRowKey(share);
    const resolved = key || `share-pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    share._listRowKey = resolved;
    return resolved;
}
